import h from 'hyperscript';

import checkIcon from '@disy/cadenza-icons/check.svg';
import errorIcon from '@disy/cadenza-icons/error.svg';
import warningIcon from '@disy/cadenza-icons/warning.svg';
import { icon } from '@disy/lib/icon';

import { Modal } from 'ui/dialog/modal/modal';

import './task-list-dialog.css';

const COMPONENT_NAME = 'd-task-list-dialog';

export type TaskListDialogResultType = 'success' | 'warning' | 'error';

interface PromiseTaskResult {
  type: TaskListDialogResultType;
  message?: string;
}

interface PromiseTask<T> {
  id?: string;
  promise: Promise<T>;
  title: string;
  processResult?: (result: T) => PromiseTaskResult;
}

export class TaskListDialog extends Modal {
  constructor() {
    super({}); // for facelift
    this.classList.add(COMPONENT_NAME);
    this.body.classList.add('d-stack-v', 'space-4');
  }

  addTask<T>(task: PromiseTask<T>) {
    const progressElement = createProgress(task);
    this.body.append(progressElement);
    return new Promise<T>((resolve, reject) => {
      task.promise
        .then((result) => {
          const taskResult: PromiseTaskResult = task.processResult ? task.processResult(result) : { type: 'success' };
          taskResult.message = taskResult.message ?? task.title;
          const resultElement = createResultElement(taskResult);
          progressElement.replaceWith(resultElement);

          resolve(result);
        })
        .catch((error) => {
          const failureElement = createFailure({
            type: 'error',
            message: task.title,
          });
          progressElement.replaceWith(failureElement);
          reject(error);
        });
    });
  }

  showCancelButton(label: string) {
    this.addCancelButton(label);
  }
}

function createProgress<T>(step: PromiseTask<T>) {
  return h('d-progress-spinner.d-progress-spinner-xs', {
    label: step.title,
  });
}

function createResultElement(result: PromiseTaskResult) {
  switch (result.type) {
    case 'success': {
      return createSuccess(result);
    }
    case 'warning': {
      return createWarning(result);
    }
    case 'error': {
      return createFailure(result);
    }
  }
}

function createSuccess(step: PromiseTaskResult) {
  return h('.success-result.d-icon-bold.d-stack-h.space-3', [icon(checkIcon, { size: 'm' }), step.message]);
}

function createWarning(step: PromiseTaskResult) {
  return h('.warning-result.d-icon-bold.d-stack-h.space-3', [icon(warningIcon, { size: 'm' }), step.message]);
}

function createFailure(step: PromiseTaskResult) {
  return h('.error-result.d-icon-bold.d-stack-h.space-3', [icon(errorIcon, { size: 'm' }), step.message]);
}

customElements.define(COMPONENT_NAME, TaskListDialog);
